import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Zap, AlertTriangle, CheckCircle2, Activity, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  on_track: { label: "On track", color: "text-emerald-700 dark:text-emerald-300", bg: "bg-emerald-100 dark:bg-emerald-900/30", border: "border-emerald-400 dark:border-emerald-700/50", icon: CheckCircle2 },
  at_risk:  { label: "At risk",  color: "text-amber-700 dark:text-amber-300",     bg: "bg-amber-100 dark:bg-amber-900/30",     border: "border-amber-400 dark:border-amber-700/50",   icon: AlertTriangle },
  critical: { label: "Critical", color: "text-rose-700 dark:text-rose-300",       bg: "bg-rose-100 dark:bg-rose-900/30",       border: "border-rose-400 dark:border-rose-700/50",     icon: AlertTriangle },
};

/** Pulse card + 1-tap check-in dialog. Designed to be embedded on
 *  ProjectDetail. Mobile-first — big tap targets, single-column form. */
const ProjectPulseCard = ({ projectId }) => {
  const [pulse, setPulse] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ hours_spent: 8, blockers_flag: false, summary_text: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/pulse`);
      setPulse(data);
    } catch (e) {
      // Pulse is optional — fail silently to avoid noise on project detail.
      console.warn("pulse load failed", e);
    }
  };

  // Fetch on mount + when the active project changes. We deliberately don't
  // capture `load` in deps — the function is recreated every render and only
  // closes over `projectId` which IS in the dep array.
  useEffect(() => {
    let cancelled = false;
    api.get(`/projects/${projectId}/pulse`)
      .then(({ data }) => { if (!cancelled) setPulse(data); })
      .catch((e) => { if (!cancelled) console.warn("pulse load failed", e); });
    return () => { cancelled = true; };
  }, [projectId]);

  const submit = async () => {
    const hrs = Number(form.hours_spent);
    if (!hrs || hrs <= 0 || hrs > 24) {
      toast.error("Hours must be between 0 and 24");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/daily-checkin`, {
        project_id: projectId,
        hours_spent: hrs,
        blockers_flag: form.blockers_flag,
        summary_text: form.summary_text,
      });
      setOpen(false);
      setForm({ hours_spent: 8, blockers_flag: false, summary_text: "" });
      toast.success("Daily pulse logged");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't log pulse");
    } finally {
      setBusy(false);
    }
  };

  if (!pulse) return null;
  const meta = STATUS_META[pulse.status] || STATUS_META.on_track;
  const StatusIcon = meta.icon;
  const negativeVariance = (pulse.variance_hours ?? 0) < 0;
  const VarianceArrow = negativeVariance ? TrendingDown : TrendingUp;
  const allocatedZero = !pulse.allocated_hours;

  return (
    <div className={`border ${meta.border} bg-[#0D1117]`} data-testid="project-pulse-card">
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#002FA7]" />
          <h3 className="font-display font-bold tracking-tight text-sm">Project Pulse</h3>
          <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${meta.bg} ${meta.color} border ${meta.border}`} data-testid="pulse-status-badge">
            {meta.label}
          </span>
        </div>
        <Button onClick={() => setOpen(true)} size="sm" className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-8 px-3" data-testid="open-daily-checkin-btn">
          <Zap className="w-3.5 h-3.5 mr-1.5" />
          {pulse.logged_today_by_user ? "Log again" : "Daily check-in"}
        </Button>
      </div>

      {allocatedZero ? (
        <div className="px-5 py-6 text-sm text-slate-400" data-testid="pulse-needs-budget">
          Set an allocated-hours budget to start tracking variance.{" "}
          <button onClick={() => {
            const v = window.prompt("Allocated hours (total fixed-fee budget):");
            if (!v) return;
            api.patch(`/projects/${projectId}/allocated-hours`, { allocated_hours: Number(v) || 0 })
              .then(() => { toast.success("Allocated hours set"); load(); })
              .catch((err) => toast.error(err.response?.data?.detail || "Failed"));
          }} className="text-[#002FA7] font-semibold underline" data-testid="pulse-set-allocated-btn">
            Set allocated hours
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1F2937] border-b border-[#1F2937]">
            <PulseTile label="Hours burned" value={`${pulse.actual_hours_burned.toLocaleString()}h`} sub={`of ${pulse.allocated_hours.toLocaleString()}h allocated`} testid="pulse-actual-hours" />
            <PulseTile label="Ideal to date" value={`${pulse.expected_hours_burned.toLocaleString()}h`} sub={`day ${pulse.days_elapsed} of ${pulse.total_days}`} testid="pulse-expected-hours" />
            <PulseTile
              label="Variance"
              value={
                <span className={negativeVariance ? "text-rose-600" : "text-emerald-700"}>
                  <VarianceArrow className="w-4 h-4 inline -mt-0.5 mr-0.5" />
                  {pulse.variance_hours > 0 ? "+" : ""}{pulse.variance_hours.toLocaleString()}h
                </span>
              }
              sub={`${pulse.variance_pct > 0 ? "+" : ""}${pulse.variance_pct}% of budget`}
              testid="pulse-variance"
            />
            <PulseTile
              label="Blockers (7d)"
              value={<span className={pulse.blocker_count_last_7d ? "text-rose-600" : "text-emerald-700"}>{pulse.blocker_count_last_7d}</span>}
              sub={`${pulse.total_metrics_logged} total taps`}
              testid="pulse-blockers"
            />
          </div>

          {pulse.recent_metrics.length === 0 ? (
            <div className="px-5 py-6 text-center text-xs text-gray-500" data-testid="pulse-empty-feed">
              No daily pulses logged yet. Tap &ldquo;Daily check-in&rdquo; above to start the feed.
            </div>
          ) : (
            <div className="divide-y divide-[#1F2937] max-h-64 overflow-y-auto" data-testid="pulse-feed">
              {pulse.recent_metrics.map((m) => (
                <div key={m.metric_id} className="px-5 py-2.5 flex items-start gap-3 hover:bg-[#030712]">
                  <div className="font-mono text-[10px] text-gray-400 w-16 shrink-0 pt-0.5">{m.logged_date}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs">
                      <span className="font-semibold text-white">{m.user_name}</span>
                      <span className="text-gray-500"> · {m.hours_spent}h</span>
                      {m.blockers_flag && (
                        <span className="ml-2 inline-flex items-center gap-1 bg-rose-50 border border-rose-300 text-rose-700 text-[10px] px-1.5 py-0.5">
                          <AlertTriangle className="w-3 h-3" /> blocker
                        </span>
                      )}
                    </div>
                    {m.summary_text && (
                      <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{m.summary_text}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-md" data-testid="daily-checkin-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gray-500">
              <Zap className="w-3.5 h-3.5" />
              Frictionless pulse
            </div>
            <DialogTitle className="font-display tracking-tight">Daily check-in</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">Three taps — hours, blocker?, optional one-liner.</p>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Hours today</Label>
              <Input
                type="number"
                min={0.5}
                max={24}
                step={0.5}
                inputMode="decimal"
                className="rounded-none mt-1 h-14 text-center font-display text-2xl font-bold"
                value={form.hours_spent}
                onChange={(e) => setForm({ ...form, hours_spent: e.target.value })}
                data-testid="checkin-hours-input"
              />
              <div className="flex gap-2 mt-2">
                {[2, 4, 6, 8].map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setForm({ ...form, hours_spent: h })}
                    className="flex-1 h-9 border border-[#1F2937] hover:border-[#002FA7] hover:text-[#002FA7] text-xs font-semibold transition-colors"
                    data-testid={`checkin-quick-${h}h`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setForm({ ...form, blockers_flag: !form.blockers_flag })}
              className={`w-full p-3 border text-left flex items-center gap-3 transition-colors ${form.blockers_flag ? "bg-rose-50 border-rose-300 text-rose-700" : "border-[#1F2937] text-slate-300 hover:border-[#374151]"}`}
              data-testid="checkin-blocker-toggle"
            >
              <div className={`w-5 h-5 border ${form.blockers_flag ? "bg-rose-500 border-rose-500" : "border-[#374151]"} flex items-center justify-center shrink-0`}>
                {form.blockers_flag && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
              </div>
              <div>
                <div className="text-sm font-semibold">I&apos;m blocked on something</div>
                <div className="text-[10px] text-gray-500 mt-0.5">Flag to alert PMO. Past 7 days fire the variance watchdog.</div>
              </div>
            </button>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">What happened? (optional)</Label>
              <Textarea
                className="rounded-none mt-1 min-h-[60px]"
                placeholder="One-liner is plenty — &lsquo;Migrated 12k contact rows, validated dedupe rules&rsquo;"
                value={form.summary_text}
                onChange={(e) => setForm({ ...form, summary_text: e.target.value })}
                data-testid="checkin-summary-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button onClick={submit} disabled={busy} className="rounded-none w-full h-11 bg-[#002FA7] hover:bg-[#002480]" data-testid="checkin-submit-btn">
              {busy ? "Logging…" : "Log pulse"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PulseTile = ({ label, value, sub, testid }) => (
  <div className="px-4 py-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{label}</div>
    <div className="text-xl font-bold mt-1 text-white">{value}</div>
    <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>
  </div>
);

export default ProjectPulseCard;
